import { Add, TrashCan } from '@carbon/icons-react';
import { Button, InlineNotification, Modal } from '@carbon/react';
import {
  asProblem,
  hasPermission,
  ListTable,
  PageHeader,
  problemMessage,
  RowAction,
  StatusTag,
  useAdmin,
  useFormat,
} from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  accountsKey,
  listAccounts,
  listProviders,
  providersKey,
  servicesApi,
  servicesKey,
  type DeliveryServiceRow,
} from './deliveryApi';
import { ServiceDialog } from './ServiceDialog';
import './delivery.scss';

/**
 * The delivery services the cashier can choose (ADR 0025, FR-DLV-01, 02): the store's own drivers and the services of
 * provider accounts, and where each is on offer.
 */
export function DeliveryServicesPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, 'ho.config.delivery_services.manage');
  const queryClient = useQueryClient();
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const [search, setSearch] = useState('');
  const services = useQuery({
    queryKey: [...servicesKey, search, paging],
    queryFn: () => servicesApi.list({ ...paging, search: search || undefined, sort: 'sortOrder' }),
    placeholderData: keepPreviousData,
  });
  const providers = useQuery({ queryKey: providersKey, queryFn: listProviders });
  const accounts = useQuery({ queryKey: accountsKey, queryFn: listAccounts });
  const [editing, setEditing] = useState<DeliveryServiceRow | 'new'>();
  const [deleting, setDeleting] = useState<DeliveryServiceRow>();
  const remove = useMutation({
    // The row carries its id and version, which is all the delete needs.
    mutationFn: (service: DeliveryServiceRow) => servicesApi.remove(service),
    onSuccess: async () => {
      setDeleting(undefined);
      await queryClient.invalidateQueries({ queryKey: servicesKey });
    },
  });

  const feeText = (s: DeliveryServiceRow) =>
    s.feeMode === 'fixed' && s.fixedFee != null
      ? `${t('delivery.services.feeModes.fixed')} · ${format.number(s.fixedFee)}`
      : t(`delivery.services.feeModes.${s.feeMode}` as I18nKey);

  // As on every list screen: search and Create in the table's toolbar, a row opens for editing, Delete on the row.
  const createButton = canEdit ? (
    <Button renderIcon={Add} disabled={!providers.data || !accounts.data} onClick={() => setEditing('new')}>
      {t('admin.create')}
    </Button>
  ) : undefined;

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('delivery.services.title')} description={t('delivery.services.intro')} />
      <ListTable<DeliveryServiceRow>
        ariaLabel={t('delivery.services.title')}
        loading={services.isLoading}
        error={services.error ? problemMessage(t, asProblem(services.error)) : undefined}
        onRetry={() => void services.refetch()}
        rows={services.data?.items ?? []}
        search={{
          label: t('admin.search'),
          onChange: (value) => {
            setSearch(value);
            setPaging((p) => ({ ...p, page: 1 }));
          },
        }}
        toolbarActions={createButton}
        onRowClick={canEdit ? (s) => setEditing(s) : undefined}
        columns={[
          { key: 'code', header: t('delivery.services.columns.code') },
          { key: 'name', header: t('delivery.services.columns.name') },
          {
            key: 'kind',
            header: t('delivery.services.columns.kind'),
            render: (s) => t(`delivery.services.kinds.${s.kind}` as I18nKey),
          },
          {
            key: 'provider',
            header: t('delivery.services.columns.provider'),
            render: (s) => (s.kind === 'external' ? [s.providerName, s.serviceType].filter(Boolean).join(' · ') : '—'),
          },
          { key: 'fee', header: t('delivery.services.columns.fee'), render: feeText },
          {
            key: 'isActive',
            header: t('delivery.services.columns.status'),
            render: (s) => (
              <StatusTag status={s.isActive ? 'active' : 'inactive'}>
                {t(s.isActive ? 'delivery.providers.active' : 'delivery.providers.inactive')}
              </StatusTag>
            ),
          },
        ]}
        rowActions={
          canEdit
            ? (s) => <RowAction action={t('admin.delete')} record={s.name} icon={TrashCan} onClick={() => setDeleting(s)} />
            : undefined
        }
        empty={search ? undefined : { heading: t('admin.emptyTitle'), description: t('delivery.services.empty') }}
        pagination={{
          ...paging,
          total: services.data?.total ?? 0,
          onChange: ({ page, pageSize }) => setPaging({ page, pageSize }),
        }}
      />

      {editing && providers.data && accounts.data && (
        <ServiceDialog
          providers={providers.data}
          accounts={accounts.data}
          service={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: servicesKey })}
        />
      )}
      {deleting && (
        <Modal
          open
          danger
          preventCloseOnClickOutside
          modalHeading={t('delivery.services.deleteTitle')}
          closeButtonLabel={t('carbon.close')}
          primaryButtonText={t('common.delete')}
          secondaryButtonText={t('common.cancel')}
          primaryButtonDisabled={remove.isPending}
          onRequestClose={() => setDeleting(undefined)}
          onRequestSubmit={() => remove.mutate(deleting)}
        >
          <p>{t('delivery.services.deleteBody', { name: deleting.name })}</p>
          {remove.error && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(remove.error))}
            />
          )}
        </Modal>
      )}
    </section>
  );
}
